<template>
  <!-- Forgot password, in the shared signed-out frame (world map left, form right). -->
  <AuthShell>
    <Form @submit="onSubmit" @invalid-submit="onInvalidSubmit" :validation-schema="schema" v-slot="{ errors }">
      <h2 class="auth-title">{{ t('auth.forgotTitle') }}</h2>
      <p class="auth-sub">{{ t('auth.forgotSub') }}</p>

      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.email') }}</span>
        <Field name="email" v-slot="{ field }">
          <input v-bind="field" type="email" name="email" autocomplete="email" placeholder="name@company.com" />
        </Field>
        <span v-if="errors.email" class="auth-error">{{ errors.email }}</span>
      </label>

      <template v-if="requiresRecaptcha">
        <Field v-model="reCaptchaV2Token" name="recaptcha" type="hidden" />
        <div class="auth-captcha" :class="{ 'is-error': Boolean(errors.recaptcha) }">
          <vue-recaptcha :sitekey="accountStore.reCaptchaSiteKey" size="normal" :hl="locale === 'zh-CN' ? 'zh-CN' : 'en'" :key="locale" theme="dark"
            :loading-timeout="recaptchaTimeout" @verify="recaptchaVerified"
            @expire="recaptchaExpired" @error="recaptchaError">
          </vue-recaptcha>
        </div>
        <span v-if="errors.recaptcha" class="auth-error" style="margin: -10px 0 12px;">{{ errors.recaptcha }}</span>
      </template>

      <button class="auth-btn" type="submit" :disabled="Boolean(isSubmitting)">{{ isSubmitting ? t('auth.sending') : t('auth.sendResetLink') }}</button>
      <div class="auth-links"><router-link to="/login">{{ t('auth.backToSignIn') }}</router-link></div>
    </Form>
  </AuthShell>
</template>

<script setup>
import { t, locale } from '@/i18n';
import { Form, Field } from 'vee-validate';
import AuthShell from '../AuthShell.vue';
import * as Yup from 'yup';
import vueRecaptcha from 'vue3-recaptcha2';
import { ref } from "vue";
import { useAccountStore, useAlertStore } from '@/stores';
const alertStore = useAlertStore();
const accountStore = useAccountStore();
// Stage the verified standard-v2 CAPTCHA on password recovery only.
// Login remains deliberately unchanged until this production flow is approved.
const requiresRecaptcha = !!process.env.VUE_APP_VUE_RECAPTCHA_V2_SITE_KEY   // reCAPTCHA once this site has its own key (.env)
const schema = Yup.object().shape({
  email: Yup.string().required(() => t('auth.v.emailRequired')).email(() => t('auth.v.emailInvalid')),
  ...(requiresRecaptcha ? { recaptcha: Yup.string().required(() => t('auth.v.captcha')) } : {}),
});

const recaptchaTimeout = ref(30000);
const reCaptchaV2Token = ref('');
const isSubmitting = ref(false);

function recaptchaVerified(response) {
  reCaptchaV2Token.value = response;
}

function recaptchaExpired() {
  reCaptchaV2Token.value = '';
}

function recaptchaError(reason) {
  reCaptchaV2Token.value = '';
  alertStore.error(t('auth.recaptchaError'));
}

async function onSubmit(values) {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  alertStore.default(t('auth.sendingLink'));
  try {
    await accountStore.forgotpassword({
      ...values,
      recaptcha: reCaptchaV2Token.value,
    });
  } catch (error) {
    console.error(error)
  } finally {
    isSubmitting.value = false;
  }
}

function onInvalidSubmit() {
  alertStore.warning(t('auth.enterValidEmail'));
}

</script>
